/**
 * The broadcast package for one story segment: chapter rail + relevance tag, context line,
 * perspective title, the player, a score bug that counts into the verified score, and an
 * illustrated representation of the verified event (lead meter, shot location, call gauge,
 * closing sequence). Display only — the StoryPlayer lifecycle owns timing and narration.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { directorClock, type DirectorResponse, type GameEvent } from '@clutch/core';
import { Headshot, TeamLogo } from '../ui/Media';
import { teamAccent } from '../ui/color';
import { reducedMotion } from '../stage/Stage';
import { COURT, feedToCourt } from '../stage/court';
import { boundedScenePlays, chapterList, decisiveSequence, recentPlays, fanSide, localTeamName, momentScene, whoToWatch, type KeyPlay, type Lens, type LeadSwing, type MomentScene, type ShotMark } from './moment-scene';
import { pick, normalizeLanguage, isChineseLanguage } from './i18n';
import { locatedShot, momentVisual, scoringSide } from './moment-presentation';
import { historyReliable } from './perspective';

export type StageScene = {
  kind: 'intro' | 'moment' | 'outcome';
  eyebrow: string;
  title: string;
  clock?: string;
  home: number | undefined;
  away: number | undefined;
  momentId?: string;
  final?: boolean;
};

const ui = (l: string) => ({
  chapter: pick({ en: 'Chapter', hi: 'अध्याय', zh: '第' }, l),
  chapterOf: (i: number, n: number) => pick({ en: `Chapter ${i} of ${n}`, hi: `अध्याय ${i} / ${n}`, zh: `第${i}章 / 共${n}章` }, l),
  lead: pick({ en: 'Lead swing', hi: 'बढ़त का झूला', zh: '分差变化' }, l),
  tied: pick({ en: 'Tied', hi: 'बराबर', zh: '平' }, l),
  shotAt: pick({ en: 'Shot location · from play-by-play', hi: 'शॉट की जगह · प्ले-बाय-प्ले से', zh: '出手位置 · 来自文字直播数据' }, l),
  made: pick({ en: 'Made', hi: 'सफल', zh: '命中' }, l),
  missed: pick({ en: 'Missed', hi: 'चूका', zh: '不中' }, l),
  pts: pick({ en: 'PTS', hi: 'अंक', zh: '分' }, l),
  soFar: pick({ en: 'so far', hi: 'अब तक', zh: '截至此时' }, l),
  gauge: (t: string, pct: number, clock: string) => pick({ en: `Model gave the ${t} ${pct}% at ${clock}`, hi: `${clock} पर मॉडल ने ${t} को ${pct}% दिया`, zh: `${clock}时模型给${t}的胜率为${pct}%` }, l),
  locked: pick({ en: 'Locked by the server', hi: 'सर्वर पर लॉक', zh: '服务器已锁定' }, l),
  sequence: pick({ en: 'The closing sequence', hi: 'आख़िरी सिलसिला', zh: '最后的回合' }, l),
  decided: pick({ en: 'How it was decided', hi: 'ऐसे तय हुआ मैच', zh: '胜负如何决出' }, l),
  watch: pick({ en: 'Who to watch', hi: 'इन पर नज़र रखें', zh: '关注球员' }, l),
  inStory: pick({ en: 'In this story', hi: 'इस कहानी में', zh: '本集看点' }, l),
  newTo: pick({ en: 'New to the NBA?', hi: 'NBA में नए हैं?', zh: 'NBA新手？' }, l),
  at: pick({ en: 'at', hi: 'बनाम', zh: '客场挑战' }, l),
  final: pick({ en: 'Final', hi: 'अंतिम', zh: '终场' }, l),
  latest: pick({ en: 'Latest plays', hi: 'ताज़ा प्ले', zh: '最新回合' }, l),
  whoWins: pick({ en: 'Who wins from here?', hi: 'यहाँ से कौन जीतेगा?', zh: '接下来谁会赢？' }, l),
  callOpens: pick({ en: 'Watch the finish and lock your call. Clutch saves the odds at that second.', hi: 'फ़िनिश देखिए और अपनी कॉल लॉक कीजिए। Clutch उसी पल की संभावना सहेजता है।', zh: '观看结局并锁定你的预测，Clutch会保存那一刻的胜率。' }, l),
  lastPlay: pick({ en: 'The last play', hi: 'आख़िरी प्ले', zh: '最后一球' }, l),
  winBy: (t: string, n: number) => pick({ en: `${t} win by ${n}`, hi: `${t} ${n} अंक से जीते`, zh: `${t}以${n}分获胜` }, l),
  illustrated: pick({ en: 'Illustrated from verified play-by-play', hi: 'सत्यापित प्ले-बाय-प्ले से बना चित्रण', zh: '根据核实的文字直播绘制' }, l),
});

export function MomentStage({ d, scene, prev, plays = [], lens = {}, l }: { d: DirectorResponse; scene: StageScene; prev?: StageScene; plays?: readonly GameEvent[]; lens?: Lens; l: string }) {
  const away = d.game.away.tricode;
  const home = d.game.home.tricode;
  const ms = useMemo(() => (scene.momentId ? momentScene(d, scene.momentId, plays, lens, l) : undefined), [d, scene.momentId, plays, lens, l]);
  const moment = d.moments.find((m) => m.momentId === scene.momentId);
  const visual = momentVisual(d, moment, plays);
  const scored = scoringSide(visual);
  const s = ui(l);
  if (scene.kind === 'intro') return <IntroScene d={d} plays={plays} lens={lens} l={l} title={scene.title} eyebrow={scene.eyebrow} />;
  if (scene.kind === 'outcome') return <OutcomeScene d={d} plays={plays} l={l} scene={scene} prev={prev} />;
  const tone = ms?.relevance.tone ?? 'neutral';
  return (
    <div className={`ms ms--${ms?.kind ?? 'moment'}${ms?.climax ? ' ms--climax' : ''}`} data-testid="moment-scene" data-relevance={ms?.relevance.kind} data-tone={tone}>
      {ms && <ChapterFlash n={ms.chapter} label={s.chapter} title={ms.title} l={l} />}
      <div className="ms__rail">
        {ms && <span className="ms__chapter" aria-label={s.chapterOf(ms.chapter, ms.chapters)}><b>{String(ms.chapter).padStart(2, '0')}</b><i>/{String(ms.chapters).padStart(2, '0')}</i></span>}
        <span className={`ms__tag ms__tag--${ms?.relevance.kind ?? 'key'} is-${tone}`} data-testid="relevance-tag">{ms?.relevance.label ?? scene.eyebrow}</span>
        {scene.clock && <span className="ms__clock">{scene.clock}</span>}
      </div>
      {ms && <p className="ms__context" data-testid="moment-context"><b>{ms.situation}</b> <span>{ms.why}</span></p>}
      <h2 className="sp__title ms__title" data-testid="story-title">{ms?.title ?? scene.title}</h2>
      <div className="ms__hero">
        {ms?.player && <PlayerPlate p={ms.player} source={d.game.source} l={l} small={ms.climax} />}
        <ScoreBug away={away} home={home} a={scene.away ?? 0} h={scene.home ?? 0} fromA={prev?.away} fromH={prev?.home} clock={scene.clock} compact={!!ms?.player} />
      </div>
      {scored && <p className="sp__impact" data-testid="story-impact" data-side={scored}>
        <b>+{visual!.points}</b><span>{scored === 'home' ? home : away} · {pick({ en: 'on this play', hi: 'इस प्ले में', zh: '本回合' }, l)}</span>
      </p>}
      {ms?.call && <CallGauge c={ms.call} l={l} />}
      {ms && ms.plays.length > 0 ? (
        <Sequence plays={ms.plays} away={away} home={home} title={d.asOf.status === 'final' ? s.sequence : s.latest} />
      ) : (
        (ms?.lead || ms?.shot) && (
          <div className="ms__viz">
            {ms.shot && <ShotCourt shot={ms.shot} away={away} home={home} label={s.shotAt} made={s.made} missed={s.missed} />}
            {ms.lead && <LeadMeter lead={ms.lead} away={away} home={home} label={s.lead} tied={s.tied} />}
          </div>
        )
      )}
      {ms?.explainer && <p className="ms__explain" data-testid="explainer"><b>{s.newTo}</b> {ms.explainer}</p>}
    </div>
  );
}

function ChapterFlash({ n, label, title, l }: { n: number; label: string; title: string; l: string }) {
  if (reducedMotion()) return null;
  return (
    <div className="ms__flash" aria-hidden="true">
      <span>{isChineseLanguage(l) ? `${label}${n}章` : `${label} ${n}`}</span>
      <b>{title}</b>
    </div>
  );
}

function IntroScene({ d, plays, lens, l, title, eyebrow }: { d: DirectorResponse; plays: readonly GameEvent[]; lens: Lens; l: string; title: string; eyebrow: string }) {
  const s = ui(l);
  const away = d.game.away.tricode;
  const home = d.game.home.tricode;
  const chapters = useMemo(() => chapterList(d, plays, lens, l), [d, plays, lens, l]);
  const side = fanSide(d, lens.followed);
  const watch = useMemo(() => (lens.familiarity ?? d.familiarity) === 'new' || !side ? whoToWatch(d, plays) : [], [d, plays, lens.familiarity, side]);
  const mine = side ? (side === 'home' ? home : away) : undefined;
  return (
    <div className="ms ms--intro" data-testid="moment-scene" data-relevance="intro">
      <div className="ms__rail"><span className={`ms__tag ${mine ? 'ms__tag--team is-for' : 'ms__tag--key'}`}>{eyebrow}</span></div>
      <div className="ms__matchup">
        <span className={mine === away ? 'is-mine' : ''}><TeamLogo tricode={away} size={watch.length ? 56 : 76} /></span>
        <i>{s.at}</i>
        <span className={mine === home ? 'is-mine' : ''}><TeamLogo tricode={home} size={watch.length ? 56 : 76} /></span>
      </div>
      <h2 className="sp__title ms__title ms__title--intro" data-testid="story-title">{title}</h2>
      {d.call && (
        <p className="ms__callchip" style={{ '--t': teamAccent(d.call.chosenTeam) } as CSSProperties}>
          <span aria-hidden="true">🔒</span>
          <b>{localTeamName(d.call.chosenTeam, l)} · {Math.round(d.call.pChoiceAtLock * 100)}%</b>
          <i>{directorClock(d.call.lockedAt.period, d.call.lockedAt.clockSec, normalizeLanguage(l))}</i>
        </p>
      )}
      {watch.length > 0 && (
        <div className="ms__watch" data-testid="who-to-watch">
          <span className="ms__label">{s.watch}</span>
          <div>{watch.map((p) => <PlayerPlate key={p.personId} p={p} source={d.game.source} l={l} small />)}</div>
        </div>
      )}
      <ol className="ms__chapters" aria-label={s.inStory}>
        {chapters.map((c, i) => (
          <li key={c.momentId} style={{ animationDelay: `${160 + i * 110}ms` }} data-testid="chapter-card" data-relevance={c.relevance.kind} data-tone={c.relevance.tone}>
            <b className="ms__chapter-n">{String(i + 1).padStart(2, '0')}</b>
            <span className="ms__chapter-body">
              <i className={`ms__tag ms__tag--${c.relevance.kind} is-${c.relevance.tone}`}>{c.relevance.label}</i>
              <strong>{c.title}</strong>
            </span>
            <em>{c.situation.split(' · ')[0]}</em>
          </li>
        ))}
      </ol>
    </div>
  );
}

function OutcomeScene({ d, plays, l, scene, prev }: { d: DirectorResponse; plays: readonly GameEvent[]; l: string; scene: StageScene; prev?: StageScene }) {
  const s = ui(l);
  const away = d.game.away.tricode;
  const home = d.game.home.tricode;
  const seq = useMemo(() => decisiveSequence(d, plays, l), [d, plays, l]);
  const last = finalShot(d, plays, l);
  const lastPlay = seq.at(-1);
  const recent = useMemo(() => (scene.final ? [] : recentPlays(d, plays, l)), [d, plays, l, scene.final]);
  const final = scene.final;
  const a = scene.away ?? 0;
  const h = scene.home ?? 0;
  const winner = a === h ? undefined : a > h ? away : home;
  return (
    <div className={`ms ms--outcome${final ? ' is-final' : ''}`} data-testid="moment-scene" data-relevance="outcome">
      <div className="ms__rail"><span className="ms__tag ms__tag--final">{scene.eyebrow}</span></div>
      <h2 className="sp__title ms__title" data-testid="story-title">{final ? s.decided : scene.title}</h2>
      <ScoreBug away={away} home={home} a={a} h={h} fromA={prev?.away} fromH={prev?.home} clock={final ? s.final.toUpperCase() : scene.clock} big />
      {final && winner && <p className="ms__margin">{s.winBy(localTeamName(winner, l), Math.abs(a - h))}</p>}
      {!final && recent.length > 0 && <Sequence plays={recent} away={away} home={home} title={s.latest} />}
      {!final && (
        <div className="ms__next" data-testid="next-up">
          <TeamLogo tricode={away} size={36} />
          <span><b>{s.whoWins}</b><small>{s.callOpens}</small></span>
          <TeamLogo tricode={home} size={36} />
        </div>
      )}
      {final && lastPlay && (
        <div className="ms__last" data-testid="final-play">
          {last?.seq === lastPlay.seq && <ShotCourt shot={last} away={away} home={home} label={s.shotAt} made={s.made} missed={s.missed} />}
          <div className="ms__last-text">
            <span className="ms__label">{s.lastPlay}</span>
            <time>{lastPlay.clock}</time>
            <b>{lastPlay.text}</b>
            <i>{away} {lastPlay.away}–{lastPlay.home} {home}</i>
          </div>
        </div>
      )}
    </div>
  );
}

/** The last shot inside the bounded ledger, with feed coordinates. */
function finalShot(d: DirectorResponse, plays: readonly GameEvent[], l: string): ShotMark | undefined {
  if (!historyReliable(d)) return undefined;
  const s = boundedScenePlays(d, plays).filter((p) => locatedShot(p) && typeof p.made === 'boolean').at(-1);
  if (!s) return undefined;
  return { eventId: s.eventId, seq: s.seq, x: s.x!, y: s.y!, made: !!s.made, ...(s.playerName ? { name: s.playerName } : {}), ...(s.side ? { side: s.side } : {}), clock: directorClock(s.period, s.clockSec, normalizeLanguage(l)) };
}

/** Score tick-up from the previous scene's value (instant under reduced motion). */
function useCount(value: number, from?: number) {
  const [n, setN] = useState(from ?? value);
  useEffect(() => {
    if (from === undefined || from === value || reducedMotion()) return setN(value);
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / 700);
      setN(Math.round(from + (value - from) * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, from]);
  return n;
}

export function ScoreBug({ away, home, a, h, fromA, fromH, clock, compact, big }: { away: string; home: string; a: number; h: number; fromA?: number; fromH?: number; clock?: string; compact?: boolean; big?: boolean }) {
  const na = useCount(a, fromA);
  const nh = useCount(h, fromH);
  const style = { '--a': teamAccent(away), '--h': teamAccent(home) } as CSSProperties;
  return (
    <div className={`sbug${compact ? ' sbug--compact' : ''}${big ? ' sbug--big' : ''}`} style={style} data-testid="story-score" aria-label={`${away} ${a}, ${home} ${h}`}>
      <span className={`sbug__team sbug__team--a${a > h ? ' is-lead' : ''}${fromA !== undefined && fromA !== a ? ' is-flash' : ''}`}>
        <TeamLogo tricode={away} size={big ? 40 : 30} /><b>{away}</b><strong>{na}</strong>
      </span>
      <span className={`sbug__team sbug__team--h${h > a ? ' is-lead' : ''}${fromH !== undefined && fromH !== h ? ' is-flash' : ''}`}>
        <TeamLogo tricode={home} size={big ? 40 : 30} /><b>{home}</b><strong>{nh}</strong>
      </span>
      {clock && <span className="sbug__clock">{clock}</span>}
    </div>
  );
}

function PlayerPlate({ p, source, l, small }: { p: NonNullable<MomentScene['player']>; source: DirectorResponse['game']['source']; l: string; small?: boolean }) {
  const s = ui(l);
  const accent = p.tricode ? teamAccent(p.tricode) : 'var(--line-strong)';
  return (
    <div className={`plate${small ? ' plate--small' : ''}`} style={{ '--t': accent } as CSSProperties} data-testid="player-plate">
      <Headshot personId={p.personId} source={source} name={p.name} size={small ? 64 : 100} ring={accent} />
      <span className="plate__text">
        <b>{p.name}</b>
        <small>{p.tricode && <TeamLogo tricode={p.tricode} size={16} />}{p.tricode ? localTeamName(p.tricode, l) : ''}</small>
        {p.points !== undefined && <strong>{p.points}<i>{s.pts}</i></strong>}
      </span>
    </div>
  );
}

function LeadMeter({ lead, away, home, label, tied }: { lead: LeadSwing; away: string; home: string; label: string; tied: string }) {
  const max = Math.max(5, Math.abs(lead.from), Math.abs(lead.to));
  const [m, setM] = useState(reducedMotion() ? lead.to : lead.from);
  useEffect(() => {
    if (reducedMotion()) return setM(lead.to);
    setM(lead.from);
    const t = setTimeout(() => setM(lead.to), 450);
    return () => clearTimeout(t);
  }, [lead.from, lead.to]);
  const pct = (Math.abs(m) / max) * 50;
  const who = (v: number) => (v === 0 ? tied : `${v > 0 ? home : away} +${Math.abs(v)}`);
  const color = m >= 0 ? teamAccent(home) : teamAccent(away);
  return (
    <figure className="meter" data-testid="lead-meter" data-from={lead.from} data-to={lead.to}>
      <figcaption className="ms__label">{label}</figcaption>
      <div className="meter__ends"><span>{away}</span><span>{home}</span></div>
      <div className="meter__track">
        <i className="meter__mid" />
        <i className="meter__fill" style={{ width: `${pct}%`, left: m >= 0 ? '50%' : `${50 - pct}%`, background: color } as CSSProperties} />
        <i className="meter__ghost" style={{ left: `${50 + (lead.from / max) * 50}%` }} />
      </div>
      <div className="meter__labels">
        <span><small>{lead.fromClock}</small><b>{who(lead.from)}</b></span>
        <span className="is-now"><small>{lead.toClock}</small><b>{who(lead.to)}</b></span>
      </div>
    </figure>
  );
}

/** Half-court (basket at top) with the shot at its reported feed coordinates. */
function ShotCourt({ shot, away, home, label, made, missed }: { shot: ShotMark; away: string; home: string; label: string; made: string; missed: string }) {
  const p = feedToCourt(shot.x, shot.y);
  // Fold to the attacked end: distance from that baseline, and across-court position.
  const fromBase = p.x < COURT.length / 2 ? p.x : COURT.length - p.x;
  const across = p.x < COURT.length / 2 ? p.y : COURT.width - p.y;
  const W = COURT.width;
  const H = Math.max(34, fromBase + 2);
  const cx = W - across;
  const cy = fromBase;
  const color = shot.side ? teamAccent(shot.side === 'home' ? home : away) : 'var(--volt)';
  const c = W / 2;
  const rim = COURT.rimFromBaseline;
  return (
    <figure className={`shot ${shot.made ? 'is-made' : 'is-miss'}`} data-testid="shot-court" data-event-id={shot.eventId} data-seq={shot.seq}>
      <svg viewBox={`-1 -1 ${W + 2} ${H + 2}`} role="img" aria-label={`${shot.name ?? ''} ${shot.made ? made : missed} ${shot.clock}`}>
        <rect className="shot__floor" x={0} y={0} width={W} height={H} rx={1} />
        <rect className="shot__paint" x={c - 8} y={0} width={16} height={19} />
        <path className="shot__line" d={`M${c - 6} 19 A6 6 0 0 0 ${c + 6} 19`} />
        <path className="shot__line" d={`M3 0 V${COURT.cornerLength} A${COURT.threeRadius} ${COURT.threeRadius} 0 0 0 ${W - 3} ${COURT.cornerLength} V0`} />
        <circle className="shot__rim" cx={c} cy={rim} r={0.9} />
        <line className="shot__line" x1={c - 3} x2={c + 3} y1={4} y2={4} />
        <g className="shot__mark" style={{ '--c': color, transformOrigin: `${cx}px ${cy}px` } as CSSProperties}>
          <circle className="shot__pulse" cx={cx} cy={cy} r={2.4} />
          {shot.made ? <circle cx={cx} cy={cy} r={1.5} fill={color} /> : <path d={`M${cx - 1.3} ${cy - 1.3} L${cx + 1.3} ${cy + 1.3} M${cx + 1.3} ${cy - 1.3} L${cx - 1.3} ${cy + 1.3}`} stroke={color} strokeWidth={0.7} />}
        </g>
      </svg>
      <figcaption><b>{shot.made ? made : missed}</b> · {label}</figcaption>
    </figure>
  );
}

function CallGauge({ c, l }: { c: NonNullable<MomentScene['call']>; l: string }) {
  const s = ui(l);
  const [shown, setShown] = useState(reducedMotion() ? c.pct : 0);
  useEffect(() => {
    if (reducedMotion()) return setShown(c.pct);
    const t = setTimeout(() => setShown(c.pct), 200);
    return () => clearTimeout(t);
  }, [c.pct]);
  const angle = -90 + (shown / 100) * 180;
  const accent = teamAccent(c.code);
  return (
    <div className={`gauge gauge--${c.result}`} style={{ '--t': accent } as CSSProperties} data-testid="story-lock">
      <svg viewBox="0 0 120 66" aria-hidden="true">
        <path className="gauge__track" d="M10 60 A50 50 0 0 1 110 60" />
        <path className="gauge__fill" d="M10 60 A50 50 0 0 1 110 60" pathLength={100} style={{ strokeDasharray: `${shown} 100` }} />
        <g className="gauge__needle" style={{ transform: `rotate(${angle}deg)` }}><line x1="60" y1="60" x2="60" y2="18" /><circle cx="60" cy="60" r="4" /></g>
      </svg>
      <span className="gauge__text">
        <small><span aria-hidden="true">🔒 </span>{s.locked}</small>
        <b>{c.team} · {c.pct}%</b>
        <i>{s.gauge(c.team, c.pct, c.clock)}</i>
      </span>
    </div>
  );
}

function Sequence({ plays, away, home, title }: { plays: KeyPlay[]; away: string; home: string; title: string }) {
  return (
    <figure className="seq" data-testid="closing-sequence">
      <figcaption className="ms__label">{title}</figcaption>
      <ol>
        {plays.map((p, i) => (
          <li key={p.seq} className={`${p.decisive ? 'is-decisive' : ''}${p.scoring ? ' is-score' : ''}`} style={{ '--t': p.side ? teamAccent(p.side === 'home' ? home : away) : 'var(--muted)', animationDelay: `${200 + i * 140}ms` } as CSSProperties}>
            <time>{p.clock.replace(/^Q\d\s|^\d?OT\s/, '')}</time>
            <span>{p.text}</span>
            <b>{away} {p.away}–{p.home} {home}</b>
          </li>
        ))}
      </ol>
    </figure>
  );
}

